import { t } from '@/i18n';

import {
  buildCreateMutationErrorMessage,
  buildCreateMutationErrorOverride,
  buildDeleteMutationErrorOverride,
  buildSaveMutationErrorOverride,
} from './shared';

/**
 * Functions, not constants. A module-level string would resolve once at import and pin the
 * wording to whatever language the app booted in; calling through resolves it when the error is
 * actually shown. Renamed from the former UPPER_SNAKE constants in !1545 — same wording, same call sites, one extra `()`.
 */
export const getAdGroupLoadErrorDescription = (): string => t('modules.campaigns.mutationErrors.adGroup.loadError');
export const getAdGroupDuplicateMessage = (): string => t('modules.campaigns.mutationErrors.adGroup.duplicate');
export const getAdGroupCreateGenericErrorMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.createGeneric');
export const getAdGroupCreateCampaignUnavailableMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.createCampaignUnavailable');
export const getAdGroupSaveGenericErrorMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.saveGeneric');
export const getAdGroupSaveUnavailableMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.saveUnavailable');
/**
 * `CampaignHasActiveFunnelsError` on a save: the payload dropped a sub-rule that is still bidding
 * without naming it (#899). Usually that means another session started one after this page
 * loaded, but a client-side path that removes rows without recording consent produces it too —
 * so the text describes what happened rather than guessing who did it. Reloading is the only
 * move that resolves either case.
 */
export const getAdGroupSaveInUseMessage = (): string => t('modules.campaigns.mutationErrors.adGroup.saveInUse');
export const getAdGroupSaveImmutableMessage = (): string => t('modules.campaigns.mutationErrors.adGroup.saveImmutable');
export const getAdGroupDeleteGenericErrorMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.deleteGeneric');
export const getAdGroupDeleteInUseMessage = (): string => t('modules.campaigns.mutationErrors.adGroup.deleteInUse');
export const getAdGroupAlreadyDeletedMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.alreadyDeleted');
export const getAdGroupCopySourceUnavailableMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.copySourceUnavailable');
/**
 * EXPECTATION_FAILED covers every `ConstraintViolationError` the copy can hit — a sub-rule the
 * target group blocks, a pricing model the creative group's ad type disallows, legacy sub-rule
 * data that no longer parses, more than one root rule. They are indistinguishable by code, so the
 * text names the source ad group instead of blaming the creative group for all four (#899).
 */
export const getAdGroupCopySourceRejectedMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.copySourceRejected');
export const getAdGroupCopyGenericErrorMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.copyGeneric');
export const getAdGroupCopyNoRuleMessage = (): string => t('modules.campaigns.mutationErrors.adGroup.copyNoRule');
export const getAdGroupCopyNoCreativeGroupsMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.copyNoCreativeGroups');
/**
 * The copy re-creates the source's pricing rules, and CPA is admin-only, so a non-admin copying a
 * CPA ad group is refused by the backend (#899). The menu entry cannot be greyed up front — the
 * campaign tree's `AdGroupListView` carries no pricing-model data — so the dialog explains it at
 * the point of failure. A 403 on this endpoint has no other cause worth distinguishing.
 */
export const getAdGroupCopyCpaForbiddenMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.copyCpaForbidden');

/**
 * EXPECTATION_FAILED on save is any `ConstraintViolationError` the pricing rule can hit — a pricing
 * model the creative group's ad type disallows (`PricingModelCompatibilityError`), a sub-rule
 * dimension the target group blocks (`PricingRuleTargetConstraintError`), a limit tree that does not
 * add up (`PricingRuleTreeValidationError`). They share one code, so the text names the three places
 * worth looking rather than guessing which fired. The rename consumer (`useAdGroupRename`) can also
 * reach `AdGroupNameTooLongError` here, though the header input's `maxlength` caps it client-side.
 *
 * Without this the code is transparent (`TRANSPARENT_ERROR_CODES`) and the raw backend string
 * reaches the user, e.g. `TargetPricingRule violates constraint: Rule at root: pricing model CPM is
 * not allowed for Push. Allowed: none` (#910). `useAdGroupSubmit` logs that raw text to the console
 * so the row/field detail is not lost with it.
 */
export const getAdGroupRuleRejectedMessage = (): string => t('modules.campaigns.mutationErrors.adGroup.ruleRejected');

/**
 * The create dialog posts a machine-built rule — `subRules: []` and default limits — so of the
 * constraint errors above only `PricingModelCompatibilityError` is actually reachable, and the only
 * field the dialog offers that can cause it is the creative group. Hence a narrower text than
 * `getAdGroupRuleRejectedMessage`: pointing at sub-rules or limits would name fields this dialog
 * does not show (#910).
 */
export const getAdGroupCreateRuleRejectedMessage = (): string =>
  t('modules.campaigns.mutationErrors.adGroup.createRuleRejected');

// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getAdGroupCreateMutationErrorOverride = (
  ...args: Parameters<ReturnType<typeof buildCreateMutationErrorOverride>>
) =>
  buildCreateMutationErrorOverride({
    duplicateMessage: getAdGroupDuplicateMessage(),
    notFoundMessage: getAdGroupCreateCampaignUnavailableMessage(),
    expectationFailedMessage: getAdGroupCreateRuleRejectedMessage(),
  })(...args);

/**
 * In-dialog variant, mirroring `getAdGroupCopyMutationErrorMessage`. The create dialog's submit
 * rejection never reaches `getAdGroupCreateMutationErrorOverride`: `EditRowDialogFooter` swallows it
 * to keep the dialog open, so `open()` resolves and the caller's catch never runs. Whatever the
 * dialog is going to show has to be composed at the throw site instead (#910).
 */
export const getAdGroupCreateMutationErrorMessage = (
  ...args: Parameters<ReturnType<typeof buildCreateMutationErrorMessage>>
) =>
  buildCreateMutationErrorMessage({
    defaultMessage: getAdGroupCreateGenericErrorMessage(),
    duplicateMessage: getAdGroupDuplicateMessage(),
    notFoundMessage: getAdGroupCreateCampaignUnavailableMessage(),
    expectationFailedMessage: getAdGroupCreateRuleRejectedMessage(),
  })(...args);

// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getAdGroupCopyMutationErrorOverride = (
  ...args: Parameters<ReturnType<typeof buildCreateMutationErrorOverride>>
) =>
  buildCreateMutationErrorOverride({
    duplicateMessage: getAdGroupDuplicateMessage(),
    notFoundMessage: getAdGroupCopySourceUnavailableMessage(),
    expectationFailedMessage: getAdGroupCopySourceRejectedMessage(),
  })(...args);

/** In-dialog variant: resolves a copy failure straight to a display string. */
// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getAdGroupCopyMutationErrorMessage = (
  ...args: Parameters<ReturnType<typeof buildCreateMutationErrorMessage>>
) =>
  buildCreateMutationErrorMessage({
    defaultMessage: getAdGroupCopyGenericErrorMessage(),
    duplicateMessage: getAdGroupDuplicateMessage(),
    notFoundMessage: getAdGroupCopySourceUnavailableMessage(),
    expectationFailedMessage: getAdGroupCopySourceRejectedMessage(),
    forbiddenMessage: getAdGroupCopyCpaForbiddenMessage(),
  })(...args);

// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getAdGroupSaveMutationErrorOverride = (
  ...args: Parameters<ReturnType<typeof buildSaveMutationErrorOverride>>
) =>
  buildSaveMutationErrorOverride({
    duplicateMessage: getAdGroupDuplicateMessage(),
    notFoundMessage: getAdGroupSaveUnavailableMessage(),
    immutableMessage: getAdGroupSaveImmutableMessage(),
    inUseMessage: getAdGroupSaveInUseMessage(),
    expectationFailedMessage: getAdGroupRuleRejectedMessage(),
  })(...args);

// Built per call, not at import: the builders copy their spec eagerly, so a resolver made at
// module load would freeze the wording to the boot language. Same builder, same spec, same
// result — only the moment of construction moves.
export const getAdGroupDeleteMutationErrorOverride = (
  ...args: Parameters<ReturnType<typeof buildDeleteMutationErrorOverride>>
) =>
  buildDeleteMutationErrorOverride({
    inUseMessage: getAdGroupDeleteInUseMessage(),
    notFoundMessage: getAdGroupAlreadyDeletedMessage(),
  })(...args);
